<template>
    <div>
        <div
            class="card"
            :class="[ status, item.type ]"
            @click="cardClickHandler($event)"
        >
            <div class="content">
                <div class="preview">
                    <img :src="item.preview">
                    <div
                        v-if="status === 'active' && item.type !== 'courses'"
                        class="status"
                        :class="item.timeStatus"
                    >{{ getStatusLabel }}</div>
                </div>

                <div class="body">
                    <div
                        v-if="item.type !== 'courses' && item.timeStatus !== 'past'"
                        class="date"
                    >
                        {{ item.datetimeBegin | isoWithTime }}
                        <template
                            v-if="item.datetimeEnd && item.datetimeEnd !== item.datetimeBegin"
                        > — {{ item.datetimeEnd | isoWithTime }}</template>
                    </div>

                    <div
                        v-if="item.name"
                        class="title"
                    >{{ item.name }}</div>

                    <div
                        v-if="item.type === 'events' && item.address && item.address.value"
                        class="address"
                    >{{ item.address.value }}</div>

                    <div
                        v-if="item.description && status === 'invite'"
                        class="description html-block"
                        v-html="item.description"
                    ></div>

                    <btn
                        v-if="status === 'active' && item.type === 'webinars'"
                        class="btn"
                        @click.native="connectHandler($event)"
                    >
                        <span class="btn-text">Подключиться</span>
                    </btn>
                </div>

                <div
                    v-if="status === 'invite'"
                    class="label"
                    :style="`color: ${getLabelColor}`"
                >{{ getInviteLabel | capitalize }}</div>
            </div>

            <div v-if="status !== 'active' || item.type === 'courses'" class="controls">
                <div
                    v-if="status === 'invite'"
                    class="link decline"
                    @click="declineHandler($event)"
                >Отклонить</div>
                <div
                    class="link main"
                    @click="actionHandler($event)"
                >{{ getMainControlLabel }}</div>
            </div>
        </div>
        <Dialog v-model="isInfoOpen" @close="checkConfirmation({ flag: false })">
            <event-info
                :item="item"
                @close="checkConfirmation({ flag: false })"
            />
        </Dialog>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import Dialog from '@/components/UI/Dialog';
import Btn from '@/components/UI/Button';
import EventInfo from './EventInfo';

const eventsGetters = [
    'getTypes',
];

const eventsActions = [
    'updateParticipantStatus',
];

export default {
    name: 'EventCard',
    components: {
        Btn,
        Dialog,
        EventInfo,
    },
    props: {
        item: {
            type: Object,
            required: true,
        },
        status: {
            type: String,
            required: true,
        },
    },
    data: () => ({
        isInfoOpen: false,
    }),
    computed: {
        getInviteLabel() {
            if (this.item.type === 'events') {
                return this.item.eventSubtype.name;
            } else {
                return this.getTypes[this.item.type].name;
            }
        },
        getLabelColor() {
            switch (this.item.type) {
                case 'webinars':
                    return '#2BBB1A';
                case 'courses':
                    return '#F89500';
                case 'events':
                    return '#E156B4';
                default:
                    return '#000000';
            }
        },
        getStatusLabel() {
            switch (this.item.timeStatus) {
                case 'now':
                    return 'Уже идет';
                case 'begins':
                    return 'Скоро начнется';
                default:
                    return '';
            }
        },
        getMainControlLabel() {
            if (this.status === 'invite') return 'Принять';
            
            switch (this.item.type) {
                case 'courses':
                    switch (this.status) {
                        case 'active':
                            return 'Продолжить';
                        case 'past':
                            return 'Посмотреть еще раз';
                        default:
                            break;
                    }
                case 'webinars':
                    if (this.status === 'future') return 'Посмотреть описание';
                case 'events':
                    if (this.status === 'future') return 'Перейти';
                default:
                    break;
            }

            return '';
        },
        ...mapGetters('events', eventsGetters),
    },
    methods: {
        cardClickHandler(e) {
            e.stopPropagation();
            if (this.status === 'invite') return;

            if (this.item.type === 'courses') {
                window.open(this.item.eventSiteUrl, '_blank');
                return;
            }

            if (this.status === 'past' || this.status === 'active') return;
            this.showInfo();
        },
        connectHandler(e) {
            e.stopPropagation();
            window.open(this.item.webinarUrl, '_blank');
        },
        async actionHandler(e) {
            if (this.status !== 'invite') return;

            e.stopPropagation();
            
            await this.updateParticipantStatus({
                eventId: this.item.id,
                action: 'to_confirmed',
            });

            this.$emit('update');
        },
        async declineHandler(e) {
            e.stopPropagation();

            await this.updateParticipantStatus({
                eventId: this.item.id,
                action: 'to_refused',
            });
            this.$emit('update');
        },
        showInfo() {
            this.isInfoOpen = true;
        },
        checkConfirmation({ flag }) {
            this.isInfoOpen = flag;
        },
        ...mapActions('events', eventsActions),
    },
};
</script>

<style lang="scss" scoped>
@import "@/styles/rem";
@import "@/styles/mrem";
@import "@/styles/variables";

.card{
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: rem(16);
    cursor: pointer;
    background-color: white;
    border-radius: rem(4);
    box-shadow: 0px 2px 8px rgba(155, 161, 166, 0.23626);
    @include on-mobile {
        padding: 0;
        box-shadow: none;
    }
    .content{
        position: relative;
        display: flex;
        align-items: flex-start;
        margin-bottom: rem(8);
        @include on-mobile {
            padding-top: mrem(32);
            flex-direction: column;
        }
        .label{
            position: absolute;
            font-weight: bold;
            font-size: rem(14);
            line-height: rem(20);
            color: #000;
            top: 0;
            right: 0;
            @include on-mobile {
                right: auto;
                left: 0;
                font-size: mrem(14);
                line-height: mrem(20);
            }
        }

        .preview{
            position: relative;
            overflow: hidden;
            width: rem(129);
            height: rem(72);
            border-radius: rem(4);
            margin-right: rem(16);
            flex-shrink: 0;
            @include on-mobile {
                width: 100%;
                height: mrem(193);
                border-radius: mrem(4);
                margin-right: 0;
                margin-bottom: mrem(16);
            }
            img{
                width: 100%;
                height: 100%;
            }
            .status{
                position: absolute;
                font-weight: bold;
                font-size: rem(12);
                line-height: rem(15);
                color: #FFFFFF;
                background: #FF5B5B;
                border: rem(2) solid #FFFFFF;
                border-radius: 0 rem(4) 0 rem(4);
                padding: rem(8) rem(16);
                right: rem(-2);
                top: rem(-2);
                user-select: none;
                @include on-mobile {
                    font-size: mrem(12);
                    line-height: mrem(15);
                    border: mrem(2) solid #FFFFFF;
                    border-radius: 0 mrem(4) 0 mrem(4);
                    padding: mrem(8) mrem(16);
                    right: mrem(-2);
                    top: mrem(-2);
                }
                &.now{
                    background: #FF5B5B;
                }
                &.begins{
                    background: #5BB2FF;
                }
            }
        }
        
        .body{
            display: flex;
            flex-direction: column;
            align-self: stretch;
            flex-grow: 10;
            .date{
                font-weight: 600;
                font-size: rem(14);
                line-height: rem(20);
                color: #98A0A9;
                margin-bottom: rem(8);
                @include on-mobile {
                    font-size: mrem(14);
                    line-height: mrem(20);
                    margin-bottom: mrem(8);
                }
            }
            .title{
                flex: 0 0 auto;
                font-weight: 600;
                font-size: rem(16);
                line-height: rem(24);
                color: #2A2626;
                margin-bottom: rem(12);
                @include on-mobile {
                    font-size: mrem(15);
                    line-height: mrem(22);
                    margin-bottom: mrem(12);
                }
            }
            .address{
                font-weight: 600;
                font-size: rem(14);
                line-height: rem(20);
                color: #98A0A9;
                margin-bottom: rem(12);
                @include on-mobile {
                    font-size: mrem(14);
                    line-height: mrem(20);
                    margin-bottom: mrem(12);
                }
            }
            .description{
                font-style: normal;
                font-weight: normal;
                font-size: rem(14);
                line-height: rem(20);
                color: #2A2626;
                margin-bottom: rem(8);
                @include on-mobile {
                    font-size: mrem(14);
                    line-height: mrem(20);
                    margin-bottom: mrem(16);
                }
            }
            .btn{
                align-self: stretch;
                width: 100%;
                margin-top: auto;
            }
            *:last-child{
                margin-bottom: 0;
            }
        }
    }

    .controls{
        display: flex;
        justify-content: flex-end;
        @include on-mobile {
            flex-direction: column-reverse;
            justify-content: flex-start;
            margin-top: mrem(16);
        }
        .link{
            font-weight: 600;
            font-size: rem(14);
            line-height: rem(20);
            padding-bottom: rem(2);
            border-bottom: rem(1) solid transparent;
            cursor: pointer;
            @include on-mobile {
                padding: mrem(12) 0;
                border-bottom: none;
                background: #FFFFFF;
                border: 1px solid #97A4BA;
                border-radius: mrem(4);
                font-weight: bold;
                font-size: mrem(14);
                line-height: mrem(16);
                color: #4663AB;
                display: flex;
                align-items: center;
                justify-content: center;
            }
            &.main{
                color: #4663AB;
                border-bottom-color: #4663AB;
                @include on-mobile {
                    border-color: #4663AB;
                }
            }
            &.decline{
                color: #97A4BA;
                margin-right: rem(12);
                border-bottom-color: #97A4BA;
                @include on-mobile {
                    margin: 0;
                    margin-top: mrem(16);
                    border-color: #97A4BA;
                }
            }
            @include on-desktop {
                &:hover{
                    border-bottom-color: transparent;
                }
            }
        }
    }

    &.active:not(.courses){
        cursor: default;
        .content{
            .preview{
                width: rem(333);
                height: rem(187);
                @include on-mobile {
                    width: 100%;
                    height: mrem(193);
                    border-radius: mrem(4);
                    margin-right: 0;
                    margin-bottom: mrem(16);
                }
            }

            .body{
                .title{
                    max-height: rem(96);
                    overflow: hidden;
                }
            }
        }
    }

    &.past:not(.courses){
        cursor: default;
    }

    &.invite{
        cursor: default;
    }

    &:not(.invite){
        @include on-desktop {
            &:hover{
                .controls{
                    .link{
                        border-bottom-color: transparent;
                    }
                }
            }
        }
    }
}
</style>